import { useState, useEffect } from 'react';
import { DashboardStats, User } from '../types/dashboard';
import QuickConvertWidget from './QuickConvertWidget';
import ApiUsageGauge from './ApiUsageGauge';
import JsonToonCard from './JsonToonCard';

interface DashboardOverviewProps {
  userId: string;
}

export default function DashboardOverview({ userId }: DashboardOverviewProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchUser();
  }, [userId]);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/dashboard/stats');
      const data = await response.json();
      setStats(data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await fetch('/api/auth/session');
      const data = await response.json();
      setUser(data.user);
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-coral-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load statistics</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total API Keys',
      value: stats.totalKeys,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
          />
        </svg>
      ),
      color: 'from-coral-500 to-coral-600',
      bgColor: 'bg-coral-50',
    },
    {
      title: 'Active Keys',
      value: stats.activeKeys,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      color: 'from-teal-500 to-teal-600',
      bgColor: 'bg-teal-50',
    },
    {
      title: 'Total Requests',
      value: stats.totalRequests.toLocaleString(),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      ),
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Requests This Month',
      value: stats.requestsThisMonth.toLocaleString(),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-50',
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Overview</h2>
        <p className="text-gray-600">Your API usage statistics and metrics</p>
      </div>

      {/* JsonToon Premium Card */}
      {user && (
        <div className="mb-8">
          <JsonToonCard
            userName={user.name}
            userEmail={user.email}
            monthlyRequests={stats.requestsThisMonth}
            lifetimeRequests={stats.totalRequests}
            monthlyTokensSaved={0}
            lifetimeTokensSaved={0}
            memberSince={user.createdAt}
            tier="FREE"
          />
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`${card.bgColor} p-3 rounded-xl`}>
                <div className={`bg-gradient-to-br ${card.color} bg-clip-text text-transparent`}>
                  {card.icon}
                </div>
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">{card.title}</h3>
            <p className="text-3xl font-bold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Interactive Widgets Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <QuickConvertWidget />
        <ApiUsageGauge stats={stats} requestLimit={10000} />
      </div>

      {/* Quick actions */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="space-y-3">
          <a
            href="/?view=api-docs"
            className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-coral-300 hover:bg-coral-50 transition-all group"
          >
            <div className="w-10 h-10 bg-coral-100 group-hover:bg-coral-200 rounded-lg flex items-center justify-center transition-colors">
              <svg
                className="w-5 h-5 text-coral-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-900">View API Documentation</p>
              <p className="text-sm text-gray-500">Learn how to use the API</p>
            </div>
          </a>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-gradient-to-r from-coral-500 to-teal-500 rounded-2xl p-6 text-white">
        <div className="flex items-start gap-4">
          <svg
            className="w-6 h-6 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h4 className="font-semibold mb-1">Free Tier Limits</h4>
            <p className="text-sm text-white/90">
              Free tier includes 10,000 requests per month per API key. Need more? Contact us about upgrading to PRO tier with 100,000 requests per month.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
